// fleet-generated: do not edit by hand
//
// Copied verbatim  : infra/fleet/artifacts/shortcuts/when.ts
// Parameters       : none — below this header, identical across the fleet
// Destination      : packages/fleet/shortcuts/src/when.ts
//
// TO CHANGE THIS FILE, edit the canonical copy in infra and re-render. It
// changes for the whole fleet at once. Editing it here is reverted by the
// next render, and the guard in this repo goes red in the meantime:
//
//     cd ../infra && node fleet/fleet.ts render --repo space-academy-app
//     node fleet/fleet.ts check --repo space-academy-app
//
// The generator runs LOCALLY and is never invoked by CI. CI only verifies
// that what is committed matches the hash in .github/fleet-lock.json, which
// also records WHICH infra commit produced it. The commit is deliberately
// not repeated here: one value, one place — and stamping it into every file
// meant a one-line template edit rewrote four files in 28 repos.
/**
 * When-clause evaluator.
 *
 * Mini-DSL inspired by VS Code's `when` expressions. Supports:
 *   - flag references: "dialog.open"
 *   - negation:        "!input.focused"
 *   - conjunction:     "a && b"
 *   - disjunction:     "a || b"  (low precedence)
 *   - parentheses:     "(a || b) && !c"
 *
 * Compiles a string clause once into a predicate; the predicate reads from a
 * mutable context. Callers may also pass a predicate function directly.
 */

import type { WhenClause, WhenContext } from "./types";

type Predicate = (ctx: WhenContext) => boolean;

const cache = new Map<string, Predicate>();

const TRUE_PREDICATE: Predicate = () => true;

export function compileWhen(clause: WhenClause | undefined): Predicate {
  if (clause === undefined) {
    return TRUE_PREDICATE;
  }
  if (typeof clause === "function") {
    return clause;
  }
  const cached = cache.get(clause);
  if (cached) {
    return cached;
  }
  const compiled = compile(clause);
  cache.set(clause, compiled);
  return compiled;
}

// ─── AST ────────────────────────────────────────────────────────────────────
type Node =
  | { type: "ref"; name: string }
  | { type: "not"; child: Node }
  | { type: "and"; left: Node; right: Node }
  | { type: "or"; left: Node; right: Node };

// ─── Tokenizer ──────────────────────────────────────────────────────────────
type Token =
  | { type: "ident"; value: string }
  | { type: "and" }
  | { type: "or" }
  | { type: "not" }
  | { type: "lparen" }
  | { type: "rparen" };

// ─── Parser (recursive descent) ─────────────────────────────────────────────
class Parser {
  private readonly tokens: readonly Token[];
  private pos = 0;

  constructor(tokens: readonly Token[]) {
    this.tokens = tokens;
  }

  atEnd(): boolean {
    return this.pos >= this.tokens.length;
  }

  peek(): Token | undefined {
    return this.tokens[this.pos];
  }

  consume(): Token | undefined {
    const t = this.tokens[this.pos];
    this.pos += 1;
    return t;
  }

  parseOr(): Node {
    let node = this.parseAnd();
    while (this.peek()?.type === "or") {
      this.consume();
      node = { type: "or", left: node, right: this.parseAnd() };
    }
    return node;
  }

  parseAnd(): Node {
    let node = this.parseUnary();
    while (this.peek()?.type === "and") {
      this.consume();
      node = { type: "and", left: node, right: this.parseUnary() };
    }
    return node;
  }

  parseUnary(): Node {
    if (this.peek()?.type === "not") {
      this.consume();
      return { type: "not", child: this.parseUnary() };
    }
    return this.parsePrimary();
  }

  parsePrimary(): Node {
    const t = this.consume();
    if (!t) {
      throw new Error("[shortcuts/when] Unexpected end of input");
    }
    if (t.type === "lparen") {
      const node = this.parseOr();
      const closing = this.consume();
      if (closing?.type !== "rparen") {
        throw new Error("[shortcuts/when] Expected ')'");
      }
      return node;
    }
    if (t.type === "ident") {
      return { type: "ref", name: t.value };
    }
    throw new Error(`[shortcuts/when] Unexpected token type: ${t.type}`);
  }
}

function compile(input: string): Predicate {
  const tokens = tokenize(input);
  const parser = new Parser(tokens);
  const node = parser.parseOr();
  if (!parser.atEnd()) {
    throw new Error(`[shortcuts/when] Unexpected token after clause: ${input}`);
  }
  return (ctx) => evaluate(node, ctx);
}

function tokenize(input: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < input.length) {
    const ch = input[i] ?? "";
    if (ch === " " || ch === "\t" || ch === "\n") {
      i += 1;
      continue;
    }
    if (ch === "(") {
      tokens.push({ type: "lparen" });
      i += 1;
      continue;
    }
    if (ch === ")") {
      tokens.push({ type: "rparen" });
      i += 1;
      continue;
    }
    if (ch === "!") {
      tokens.push({ type: "not" });
      i += 1;
      continue;
    }
    if (ch === "&" && input[i + 1] === "&") {
      tokens.push({ type: "and" });
      i += 2;
      continue;
    }
    if (ch === "|" && input[i + 1] === "|") {
      tokens.push({ type: "or" });
      i += 2;
      continue;
    }
    if (/[A-Za-z_]/u.test(ch)) {
      let j = i + 1;
      while (j < input.length && /[A-Za-z0-9._-]/u.test(input[j] ?? "")) {
        j += 1;
      }
      tokens.push({ type: "ident", value: input.slice(i, j) });
      i = j;
      continue;
    }
    throw new Error(`[shortcuts/when] Unexpected character "${ch}" at position ${i}`);
  }
  return tokens;
}

function evaluate(node: Node, ctx: WhenContext): boolean {
  switch (node.type) {
    case "ref": {
      return Boolean(ctx[node.name]);
    }
    case "not": {
      return !evaluate(node.child, ctx);
    }
    case "and": {
      return evaluate(node.left, ctx) && evaluate(node.right, ctx);
    }
    case "or": {
      return evaluate(node.left, ctx) || evaluate(node.right, ctx);
    }
    default: {
      const exhaustive: never = node;
      throw new Error(`[shortcuts/when] Unreachable: ${JSON.stringify(exhaustive)}`);
    }
  }
}
